'use client';

import pathlib from 'path';

import { AssetPath, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';

import { scheduleDistRefreshCallback } from './coordination';

import type { IProject } from '../../store/project';
import type { FileChangeEvent } from '@/lib/storage/interfaces';

import { editorAssets, editorStore } from '@/editor/store/editorStore';

/** Per-directory asset tree refresh coalescing. */
const DIRECTORY_DEBOUNCE_MS = 200;

const VIRTUAL_ENTRY_RELATIVE = 'src/.virtual-entry.ts';

const directoryDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

// --- Build-stamp refresh bridge (lazy-loads externalRefresh on first stamp event) ---

let buildStampRefreshHandler: (() => void) | null = null;
let importInFlight: Promise<void> | null = null;
/** Merged pending stamp intent while the external refresh module is loading. */
let pendingNotify = false;
/** Invalidates stale async import completions after teardown. */
let refreshHandlerGeneration = 0;

function dispatchBuildStampRefresh(): void {
  buildStampRefreshHandler?.();
}

function replayPendingNotifyIfNeeded(): void {
  if (!pendingNotify || buildStampRefreshHandler == null) {
    return;
  }
  pendingNotify = false;
  dispatchBuildStampRefresh();
}

async function ensureBuildStampRefreshHandlerLoaded(): Promise<void> {
  if (buildStampRefreshHandler != null) {
    replayPendingNotifyIfNeeded();
    return;
  }
  if (importInFlight != null) {
    await importInFlight;
    replayPendingNotifyIfNeeded();
    return;
  }

  const generationAtStart = refreshHandlerGeneration;
  importInFlight = (async () => {
    try {
      const { queueExternalRefreshAndDrainSoon } = await import(
        '@/editor/core/project-file-watch/externalRefresh'
      );
      if (generationAtStart !== refreshHandlerGeneration) {
        return;
      }
      buildStampRefreshHandler = queueExternalRefreshAndDrainSoon;
      replayPendingNotifyIfNeeded();
    } catch (error) {
      if (generationAtStart === refreshHandlerGeneration) {
        console.warn('[FileWatcher] Failed to load external refresh handler:', error);
      }
      // Keep pendingNotify so the next stamp event retries the import.
    } finally {
      if (generationAtStart === refreshHandlerGeneration) {
        importInFlight = null;
      }
    }
  })();

  await importInFlight;
}

function notifyBuildStampRefresh(): void {
  if (buildStampRefreshHandler != null) {
    dispatchBuildStampRefresh();
    return;
  }
  pendingNotify = true;
  void ensureBuildStampRefreshHandlerLoaded();
}

export function clearBuildStampRefreshHandler(): void {
  refreshHandlerGeneration += 1;
  buildStampRefreshHandler = null;
  importInFlight = null;
  pendingNotify = false;
}

/** Clears debounced directory refresh timers (e.g. when leaving the editor route). */
export function clearPendingDirectoryRefreshes(): void {
  for (const timer of directoryDebounceTimers.values()) {
    clearTimeout(timer);
  }
  directoryDebounceTimers.clear();
}
// --- File change routing ---

function normaliseFsPath(p: string): string {
  return p.replace(/\\/g, '/');
}

function shouldIgnoreEventPath(relativePath: string): boolean {
  const n = normaliseFsPath(relativePath);
  return n === VIRTUAL_ENTRY_RELATIVE || n.endsWith(`/${VIRTUAL_ENTRY_RELATIVE}`);
}

/** Build pipeline touches this file; match SDK watcher behaviour. */
const BUILD_STAMP_RELATIVE = '.editor/build-stamp.json';

function isBuildStampFile(relativePath: string): boolean {
  return normaliseFsPath(relativePath) === BUILD_STAMP_RELATIVE;
}

function scheduleRefreshAfterBuildFromStamp(): void {
  scheduleDistRefreshCallback(() => {
    notifyBuildStampRefresh();
  });
}

function scheduleDirectoryRefresh(changeDir: string): void {
  if (!editorStore.project.isProjectOpened) {
    return;
  }
  const existing = directoryDebounceTimers.get(changeDir);
  if (existing != null) {
    clearTimeout(existing);
  }
  directoryDebounceTimers.set(
    changeDir,
    setTimeout(() => {
      directoryDebounceTimers.delete(changeDir);
      if (!editorStore.project.isProjectOpened) {
        return;
      }
      void editorAssets.assetTree.refreshDirectory(changeDir, false);
    }, DIRECTORY_DEBOUNCE_MS)
  );
}

/**
 * Handles file server WebSocket events: coalesced refresh when `.editor/build-stamp.json` changes,
 * debounced asset tree updates, and ignores noisy generated paths such as `src/.virtual-entry.ts`.
 */
export function createFileChangeHandler(_project: IProject): (event: FileChangeEvent) => void {
  return (event: FileChangeEvent) => {
    if (!editorStore.project.isProjectOpened) {
      return;
    }
    if (shouldIgnoreEventPath(event.path)) {
      return;
    }

    if (isBuildStampFile(event.path)) {
      scheduleRefreshAfterBuildFromStamp();
      return;
    }

    const changePath = AssetPath.fromString(AssetPath.join(PROJECT_PATH_PREFIX, event.path));
    const changeDir = pathlib.dirname(changePath.initialPath);
    scheduleDirectoryRefresh(changeDir);
  };
}


